import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { NormalizedLoad } from 'types/Load';
import { Maybe } from 'types/UtilityTypes';
import { TMS } from 'types/enums/Integrations';
import { normalizeTimezone } from 'utils/normalizeTimezone';

import {
  CarrierCheckInInputs,
  CheckInDetails,
  CheckInName,
  CheckInSummaryItem,
  TimeUnits,
} from './types';

dayjs.extend(utc);
dayjs.extend(timezone);

export { CheckInName } from './types';

// ============================================================================
// Formatting Functions
// ============================================================================

export const formatNormalizedTimestamp = (
  date: Dayjs,
  format: string,
  targetTimezone?: string
): string => {
  if (targetTimezone) {
    return (
      date.tz(targetTimezone).format(format) +
      ' ' +
      date.tz(targetTimezone).format('z')
    );
  }
  return date.format(format);
};

export const getSingularOrPlural = (value: number, unit: string) => {
  if (value === 1) {
    switch (unit) {
      case TimeUnits.Minutes:
        return 'minute';
      case TimeUnits.Hours:
        return 'hour';
      case TimeUnits.Days:
        return 'day';
      default:
        return unit.toLowerCase();
    }
  }

  return unit.toLowerCase();
};

export const calculateEmailSendTime = (
  duration: number,
  timeUnit: TimeUnits,
  baseTime: Dayjs,
  timezone: string,
  isBefore: boolean = false
) => {
  let sendTime = baseTime;
  switch (timeUnit) {
    case TimeUnits.Minutes:
      sendTime = isBefore
        ? sendTime.subtract(duration, 'minute')
        : sendTime.add(duration, 'minute');
      break;
    case TimeUnits.Hours:
      sendTime = isBefore
        ? sendTime.subtract(duration, 'hour')
        : sendTime.add(duration, 'hour');
      break;
    case TimeUnits.Days:
      sendTime = isBefore
        ? sendTime.subtract(duration, 'day')
        : sendTime.add(duration, 'day');
      break;
  }

  return formatNormalizedTimestamp(dayjs(sendTime), 'MM/DD HH:mm', timezone);
};

export const calculateSingleEmailTime = (
  startTime: dayjs.Dayjs,
  endTime: dayjs.Dayjs,
  timezone: string
) => {
  const midpoint = startTime.add(endTime.diff(startTime) / 2);
  return formatNormalizedTimestamp(midpoint, 'MM/DD HH:mm', timezone);
};

export const calculateInTransitEmails = (
  start: string,
  end: string,
  frequency: number,
  unit: TimeUnits,
  load: NormalizedLoad
) => {
  const startTime = dayjs.tz(start, load.pickup.timezone);
  const endTime = dayjs.tz(end, load.consignee.timezone);

  let totalIntervals = 0;
  switch (unit) {
    case TimeUnits.Minutes:
      totalIntervals = endTime.diff(startTime, 'minute') / frequency;
      break;
    case TimeUnits.Hours:
      totalIntervals = endTime.diff(startTime, 'hour') / frequency;
      break;
    case TimeUnits.Days:
      totalIntervals = endTime.diff(startTime, 'day') / frequency;
      break;
  }

  return Math.max(Math.floor(totalIntervals), 0);
};

export const calculateSendTimeForCheckIn = (
  checkInName: CheckInName,
  duration: Maybe<string | number>,
  timeUnit: Maybe<TimeUnits>,
  frequency: Maybe<string | number>,
  isApptInPast: boolean,
  pickupTime: dayjs.Dayjs,
  dropoffTime: dayjs.Dayjs,
  load: NormalizedLoad
): Maybe<string> => {
  let sendTime: Maybe<string> = null;

  switch (checkInName) {
    case CheckInName.Dispatch:
      if (timeUnit) {
        sendTime = calculateEmailSendTime(
          Number(duration),
          timeUnit,
          dayjs(),
          dayjs.tz.guess()
        );
      }
      break;
    case CheckInName.Pickup:
      if (timeUnit) {
        sendTime = calculateEmailSendTime(
          isApptInPast ? Number(duration) : 30,
          timeUnit,
          isApptInPast ? dayjs() : pickupTime,
          isApptInPast ? dayjs.tz.guess() : load.pickup.timezone,
          !isApptInPast
        );
      }
      break;
    case CheckInName.Dropoff:
      if (timeUnit) {
        sendTime = calculateEmailSendTime(
          isApptInPast ? Number(duration) : 30,
          timeUnit,
          isApptInPast ? dayjs() : dropoffTime,
          isApptInPast ? dayjs.tz.guess() : load.consignee.timezone,
          !isApptInPast
        );
      }
      break;
    case CheckInName.AfterPickup:
      if (duration && timeUnit) {
        sendTime = calculateEmailSendTime(
          Number(duration),
          timeUnit,
          isApptInPast ? dayjs() : pickupTime,
          isApptInPast ? dayjs.tz.guess() : load.pickup.timezone
        );
      }
      break;
    case CheckInName.AfterDropoff:
      if (duration && timeUnit) {
        sendTime = calculateEmailSendTime(
          Number(duration),
          timeUnit,
          isApptInPast ? dayjs() : dropoffTime,
          isApptInPast ? dayjs.tz.guess() : load.consignee.timezone
        );
      }
      break;
    case CheckInName.InTransit: {
      const startTime = pickupTime.isAfter(dayjs()) ? pickupTime : dayjs();
      const frequencyNum = Number(frequency);
      if (frequency && timeUnit && frequencyNum > 0) {
        const startTimeWithTZ = formatNormalizedTimestamp(
          startTime,
          'MM/DD HH:mm',
          load.pickup.timezone
        );
        const endTimeWithTZ = formatNormalizedTimestamp(
          dropoffTime,
          'MM/DD HH:mm',
          load.consignee.timezone
        );

        const emailCount = calculateInTransitEmails(
          startTimeWithTZ,
          endTimeWithTZ,
          frequencyNum,
          timeUnit,
          load
        );

        if (emailCount === 1) {
          const singleEmailTime = calculateSingleEmailTime(
            startTime,
            dropoffTime,
            load.pickup.timezone
          );
          sendTime = `Send 1 email at ${singleEmailTime}`;
        } else {
          const timeUnitDisplay = getSingularOrPlural(frequencyNum, timeUnit);
          sendTime = `Send 1 email every ${frequencyNum} ${timeUnitDisplay} between ${startTimeWithTZ} and ${endTimeWithTZ}. Scheduling ${emailCount} total emails.`;
        }
      } else {
        sendTime =
          'Please specify a valid frequency and time unit for in-transit emails.';
      }
      break;
    }
    default:
      // CheckInName.PodCollection or any other case - sendTime remains null
      break;
  }

  return sendTime;
};

// ============================================================================
// Validation/Check Functions
// ============================================================================

export const hasAnyCheckIn = (data: CarrierCheckInInputs): boolean => {
  return Object.values(data).some(
    (value) =>
      typeof value === 'object' && 'checkIn' in value && value.checkIn === true
  );
};

// ============================================================================
// TMS Helper Functions
// ============================================================================

export const showOutDatetimeByTMS = (
  tmsName: string,
  status: string
): boolean => {
  switch (tmsName) {
    case TMS.Relay:
      return (
        status.toLowerCase() == 'mark loaded' ||
        status.toLowerCase() == 'mark delivered'
      );

    default:
      return false;
  }
};

export const showNextStopETAByTMS = (
  tmsName: string,
  status: string
): boolean => {
  switch (tmsName) {
    case TMS.Relay: {
      const s = status?.toLowerCase();
      return s.includes('in transit') || s.includes('dispatch');
    }

    default:
      return false;
  }
};

export const showReasonFieldByTMS = (
  tmsName: string,
  status: string
): boolean => {
  switch (tmsName) {
    case TMS.Aljex:
      return true;

    case TMS.Relay: {
      const s = status?.toLowerCase();
      return !s.includes('note') && !s.includes('dispatch');
    }

    case TMS.Turvo:
      return false;

    default:
      return false;
  }
};

export const requireCityStateByTMS = (
  tmsName: string,
  status: string,
  isMilestone: boolean
): boolean => {
  switch (tmsName) {
    case TMS.Aljex:
      return !isMilestone;

    case TMS.Relay:
      return !isMilestone && !status?.toLowerCase().includes('note');

    case TMS.Turvo:
      return !isMilestone;

    case TMS.Tai:
      return true;

    default:
      return false;
  }
};

export const showExceptionFieldByTMS = (
  tmsName: string,
  status: string
): boolean => {
  switch (tmsName) {
    case TMS.Relay:
      return status?.toLowerCase().includes('note');

    default:
      return false;
  }
};

export const showOnTimeFieldByTMS = (
  tmsName: string,
  status: string,
  isMilestone: boolean
): boolean => {
  switch (tmsName) {
    case TMS.Relay:
      return isMilestone || status.toLowerCase().includes('note');

    default:
      return false;
  }
};

// ============================================================================
// Email Schedule Calculation Functions
// ============================================================================

export const buildCheckInSummaryItems = (
  dispatch: Maybe<CheckInDetails>,
  pickup: Maybe<CheckInDetails>,
  afterPickup: Maybe<CheckInDetails>,
  inTransit: Maybe<
    Omit<CheckInDetails, 'duration' | 'sendTime'> & { frequency: number }
  >,
  dropoff: Maybe<CheckInDetails>,
  afterDropoff: Maybe<CheckInDetails>,
  load: NormalizedLoad,
  isPickupInPast: boolean,
  isDropoffInPast: boolean
): CheckInSummaryItem[] => {
  return [
    {
      name: CheckInName.Dispatch,
      label: 'Confirm dispatch',
      data: dispatch,
      baseTime: dayjs(),
      timezone: dayjs.tz.guess(),
      isBefore: false,
    },
    {
      name: CheckInName.Pickup,
      label: 'At pickup appointment',
      data: pickup,
      baseTime: isPickupInPast ? dayjs() : dayjs(load.pickup.apptStartTime),
      timezone: isPickupInPast
        ? dayjs.tz.guess()
        : normalizeTimezone(load.pickup.timezone) || dayjs.tz.guess(),
      isBefore: !isPickupInPast,
      defaultDuration: isPickupInPast ? pickup?.duration : 30,
    },
    {
      name: CheckInName.AfterPickup,
      label: 'Confirm loaded',
      data: afterPickup,
      baseTime: isPickupInPast ? dayjs() : dayjs(load.pickup.apptStartTime),
      timezone: isPickupInPast
        ? dayjs.tz.guess()
        : normalizeTimezone(load.pickup.timezone) || dayjs.tz.guess(),
      isBefore: false,
    },
    {
      name: CheckInName.InTransit,
      label: 'In-Transit',
      data: inTransit,
      baseTime: null,
      timezone: null,
      isBefore: false,
      isInTransit: true,
    },
    {
      name: CheckInName.Dropoff,
      label: 'At dropoff appointment',
      data: dropoff,
      baseTime: isDropoffInPast ? dayjs() : dayjs(load.consignee.apptStartTime),
      timezone: isDropoffInPast
        ? dayjs.tz.guess()
        : normalizeTimezone(load.consignee.timezone) || dayjs.tz.guess(),
      isBefore: !isDropoffInPast,
      defaultDuration: isDropoffInPast ? dropoff?.duration : 30,
    },
    {
      name: CheckInName.AfterDropoff,
      label: 'Confirm unloaded',
      data: afterDropoff,
      baseTime: isDropoffInPast ? dayjs() : dayjs(load.consignee.apptStartTime),
      timezone: isDropoffInPast
        ? dayjs.tz.guess()
        : normalizeTimezone(load.consignee.timezone) || dayjs.tz.guess(),
      isBefore: false,
    },
  ];
};

export const calculateSendTimeForSummary = (
  checkIn: CheckInSummaryItem,
  load: NormalizedLoad
): Maybe<string> => {
  if (!checkIn.data?.checkIn) {
    return null;
  }

  if (checkIn.isInTransit) {
    const pickupTime = dayjs(load.pickup.apptStartTime);
    const dropoffTime = dayjs(load.consignee.apptStartTime);
    const startTime = pickupTime.isAfter(dayjs()) ? pickupTime : dayjs();

    if (
      'frequency' in checkIn.data &&
      checkIn.data.frequency &&
      checkIn.data.timeUnit &&
      checkIn.data.frequency > 0
    ) {
      const startTimeWithTZ = formatNormalizedTimestamp(
        startTime,
        'MM/DD HH:mm',
        load.pickup.timezone
      );
      const endTimeWithTZ = formatNormalizedTimestamp(
        dropoffTime,
        'MM/DD HH:mm',
        load.consignee.timezone
      );

      const duration = checkIn.data.frequency;
      const unit = checkIn.data.timeUnit;
      let totalIntervals = 0;

      switch (unit) {
        case TimeUnits.Minutes:
          totalIntervals = dropoffTime.diff(startTime, 'minute') / duration;
          break;
        case TimeUnits.Hours:
          totalIntervals = dropoffTime.diff(startTime, 'hour') / duration;
          break;
        case TimeUnits.Days:
          totalIntervals = dropoffTime.diff(startTime, 'day') / duration;
          break;
      }

      const emailCount = Math.max(Math.floor(totalIntervals), 0);

      if (emailCount === 1) {
        const midpoint = startTime.add(dropoffTime.diff(startTime) / 2);
        const singleEmailTime = formatNormalizedTimestamp(
          midpoint,
          'MM/DD HH:mm',
          load.pickup.timezone
        );
        return `1 email at ${singleEmailTime}`;
      } else {
        const timeUnitDisplay =
          duration === 1
            ? unit === TimeUnits.Minutes
              ? 'minute'
              : unit === TimeUnits.Hours
                ? 'hour'
                : 'day'
            : unit.toLowerCase();
        return `${emailCount} emails every ${duration} ${timeUnitDisplay} (${startTimeWithTZ} - ${endTimeWithTZ})`;
      }
    }
    return null;
  }

  if (!checkIn.baseTime || !checkIn.timezone) {
    return null;
  }

  const duration =
    checkIn.defaultDuration ??
    ('duration' in checkIn.data ? checkIn.data.duration : undefined) ??
    0;
  const timeUnit =
    'timeUnit' in checkIn.data ? checkIn.data.timeUnit : undefined;

  if (!duration && !checkIn.defaultDuration) {
    return null;
  }

  let sendTime = checkIn.baseTime;
  switch (timeUnit) {
    case TimeUnits.Minutes:
      sendTime = checkIn.isBefore
        ? sendTime.subtract(duration, 'minute')
        : sendTime.add(duration, 'minute');
      break;
    case TimeUnits.Hours:
      sendTime = checkIn.isBefore
        ? sendTime.subtract(duration, 'hour')
        : sendTime.add(duration, 'hour');
      break;
    case TimeUnits.Days:
      sendTime = checkIn.isBefore
        ? sendTime.subtract(duration, 'day')
        : sendTime.add(duration, 'day');
      break;
  }

  return formatNormalizedTimestamp(sendTime, 'MM/DD HH:mm', checkIn.timezone);
};
